import * as microsoftTeams from "@microsoft/teams-js";
import { IRestorableScheduleTeam, ITeamInfoEntity, TeamCategoryType } from "sh-models";

/**
 * Viewstate for the New Team Setup UX
 */

/**
 * The mode of team setup operation
 */
export enum TeamSetupMode {
    CreateNewSchedule = "createNewSchedule",
    RestoreOriginalSchedule = "restoreOriginalSchedule"
}

/**
 * Member role for the provision schedule request
 */
export type ProvisionScheduleMemberRole = "owner" | "member";

/**
 * Member entry for the provision schedule request body
 */
export interface ProvisionScheduleMember {
    userId: string;
    role: ProvisionScheduleMemberRole;
}

/**
 * Details needed to create a new team
 */
export interface TeamSetupInfo {
    msTeamInfo: microsoftTeams.TeamInformation; // MS Team to create a schedule team for
    scheduleTeamInfo?: ITeamInfoEntity; // Associated schedule team info (if exists and is soft-deleted)
    timeZoneOlsonCode: string; // Team timezone
    setupMode?: TeamSetupMode; // The mode of setup - creating new or restoring
    teamDisplayName?: string; // Display name of a new team being created (NewUserOnboarding flow)
    members?: ProvisionScheduleMember[]; // Members being added to the new team (NewUserOnboarding flow)
    schedulingGroupName?: string; // Name of the first scheduling group (mirrors the first channel name from CreateTeamName)
    channelName?: string; // Name of the first channel for the new team (NewUserOnboarding flow)
}

export enum TeamSetupPickerStepState {
    Invalid = -1,
    ScheduleTeamPicker,
    MSTeamPicker,
    CreateRestoreSchedule,  // New step for create/restore schedule dialog (used when DeleteScheduleEnabled FF is on)
    TimeZonePicker,
    StartTour,
    UnknownUserWelcome,
    UnknownUserTeamPicker,
    RestorableSchedulesPicker,
    CreateTeamName,
    AddTeamMembers
}

export interface TeamSetupPickerStore {
    setupSequence: Array<TeamSetupPickerStepState>; // The sequence of steps to complete the team setup
    currentSetupStepIndex: number; // The current step index in the sequence of steps
    teamSetupInfo: TeamSetupInfo; // Details entered by user to create the team
    isProvisionTeamInProgress: boolean; // true if a new schedule team is being provisioned
    errorMessage: string; // Error message string to display. Set to null for no error message.
    showStepIndicator: boolean; // true shows the step indicator component in the setup dialog
    selectedTeamCategoryType?: TeamCategoryType; // The type of team category selected
    restorableSchedules?: IRestorableScheduleTeam[]; // Restorable schedules for the currently selected team
}
